import type { FC } from "react";

import { Box, Row, type SystemStyleObject, Text } from "@hightouch/htui";

import type { TraitDefinitionWithoutFilterableColumnsFragment } from "src/graphql";
import { ConditionType, type PropertyCondition } from "src/types/visual";

import { AndOrToggleButton } from "./condition-buttons";
import { PropertyFilter, type PropertyFilterProps } from "./property-filter";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";

type NestedPropertyFilterProps = {
  traits?: TraitDefinitionWithoutFilterableColumnsFragment[];
  condition: PropertyCondition;
  isFirstCondition?: boolean;
  isWrappedWithAndCondition?: boolean;
  onRemove?(): void;
  onToggleWrappingConditionType(
    conditionType: ConditionType.And | ConditionType.Or,
  ): void;
  containerSx?: SystemStyleObject;
  hideAndOrToggleButton?: boolean;
  level?: number;
};

export const NestedPropertyFilter: FC<
  Readonly<
    NestedPropertyFilterProps &
      // This component overrides the columns for its children, so disallow `columns` from being passed in.
      Omit<PropertyFilterProps, "columns" | "lhsPropertyFilterVariant"> & {
        columns?: never;
        lhsPropertyFilterVariant: "dropdown" | "text";
      }
  >
> = ({
  condition,
  isFirstCondition = false,
  isWrappedWithAndCondition = true,
  onUpdate,
  onRemove,
  onToggleWrappingConditionType,
  containerSx,
  hideAndOrToggleButton = false,
  level,
  ...props
}) => {
  const { columns, isLoading } = useFilterableOptionsContext();

  return (
    <Row sx={containerSx} minWidth={0}>
      {!props.isNested && isFirstCondition && (
        <Box mt={1.5} mr={2}>
          <Text color="text.secondary">Where</Text>
        </Box>
      )}
      {!isFirstCondition && !hideAndOrToggleButton && (
        <AndOrToggleButton
          isDisabled={props.isDisabled}
          conditionType={
            isWrappedWithAndCondition ? ConditionType.And : ConditionType.Or
          }
          onClick={() =>
            onToggleWrappingConditionType(
              isWrappedWithAndCondition ? ConditionType.Or : ConditionType.And,
            )
          }
        />
      )}
      <Box flex={1} minWidth={60}>
        <PropertyFilter
          {...props}
          isNested
          isLoading={isLoading || props.isLoading}
          columns={columns}
          condition={condition}
          onUpdate={onUpdate}
          onRemove={onRemove}
          isWrappedWithAndCondition={isWrappedWithAndCondition}
          level={level}
        />
      </Box>
    </Row>
  );
};
